import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD, fmtMonths } from "@/lib/format";

export default function RefinancePage() {
    const [balance, setBalance] = useState(320000);
    const [oldRate, setOldRate] = useState(7.5);
    const [oldRemaining, setOldRemaining] = useState(25);
    const [newRate, setNewRate] = useState(5.9);
    const [newTerm, setNewTerm] = useState(30);
    const [closingCosts, setClosingCosts] = useState(4500);

    const result = useMemo(() => {
        const bal = toNum(balance);
        const oldM = monthlyPayment(bal, toNum(oldRate), Math.max(1, toNum(oldRemaining)));
        const newM = monthlyPayment(bal + toNum(closingCosts), toNum(newRate), Math.max(1, toNum(newTerm)));
        const savings = oldM - newM;
        const breakEven = savings > 0 ? toNum(closingCosts) / savings : Infinity;
        const totalOld = oldM * toNum(oldRemaining) * 12;
        const totalNew = newM * toNum(newTerm) * 12;
        return { oldM, newM, savings, breakEven, lifetime: totalOld - totalNew };
    }, [balance, oldRate, oldRemaining, newRate, newTerm, closingCosts]);

    return (
        <CalculatorShell
            slug="refinance"
            explainer={
                               <>
                    <h2>How this refinance calculator works</h2>
                    <p>
                        A refinance calculator compares your current mortgage to a new loan to show whether
                        refinancing saves money and how long it takes to break even on closing costs. Use it
                        any time rates drop or your credit profile improves.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Current balance:</strong> your remaining principal — not the original loan amount — is what you're refinancing.</li>
                        <li><strong>New monthly payment:</strong> calculated from the new rate and term applied to the current balance.</li>
                        <li><strong>Monthly savings:</strong> the difference between your current payment and the new payment.</li>
                        <li><strong>Break-even point:</strong> closing costs divided by monthly savings — the number of months before you come out ahead.</li>
                        <li><strong>Total interest comparison:</strong> shows life-of-loan cost for both scenarios so you can weigh short-term savings against long-term cost.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>A break-even under 24 months is generally a strong case for refinancing.</li>
                        <li>Extending the term lowers your payment but can increase total interest — model both same-term and shorter-term options.</li>
                        <li>Cash-out refinancing adds to your balance; only consider it if the purpose clearly justifies the added cost.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Current loan balance" prefix="$" value={balance} onChange={setBalance} dataTestId="calc-input-balance" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Current rate" suffix="%" value={oldRate} onChange={setOldRate} step={0.125} dataTestId="calc-input-oldrate" />
                        <Field label="Years remaining" value={oldRemaining} onChange={setOldRemaining} dataTestId="calc-input-remaining" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="New rate" suffix="%" value={newRate} onChange={setNewRate} step={0.125} dataTestId="calc-input-newrate" />
                        <Field label="New term (years)" value={newTerm} onChange={setNewTerm} dataTestId="calc-input-newterm" />
                    </div>
                    <Field label="Closing costs" prefix="$" value={closingCosts} onChange={setClosingCosts} dataTestId="calc-input-closing" />
                </div>
                <div className="lg:col-span-2 space-y-4">
                    <ResultStat
                        label="Monthly savings"
                        value={result.savings}
                        format={(n) => fmtUSD(n)}
                        secondary={[
                            { label: "New payment", value: result.newM, format: (n) => fmtUSD(n), testId: "calc-result-newpayment" },
                            { label: "Old payment", value: result.oldM, format: (n) => fmtUSD(n), testId: "calc-result-oldpayment" },
                        ]}
                    />
                    <div className="bg-card border border-border rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            Break-even
                        </div>
                        <div className="font-mono tabular text-3xl text-primary mt-2" data-testid="calc-result-breakeven">
                            {fmtMonths(result.breakEven)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Time to recover closing costs</div>
                        <div className="mt-4 pt-4 border-t border-border">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                Lifetime savings
                            </div>
                            <div className="font-mono tabular text-xl text-primary mt-1" data-testid="calc-result-lifetime">
                                {fmtUSD(result.lifetime, { decimals: 0 })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CalculatorShell>
    );
}
